import { useCallback, useRef } from 'react';
import { createFileRoute, useBlocker } from '@tanstack/react-router';
import { InfoScreen } from '@/components/patientApp/infoScreen';
import { VasScaleScreen } from '@/components/patientApp/scaleScreen';
import { usePatientApp } from '@/hooks/usePatientApp';
import { ProcedureScreen } from '@/types/procedure';
import type {
  ScaleConfig,
  LoadingConfig,
  ImageDisplayConfig,
  InfoScreenConfig,
} from '@/types/procedure';
import { FocusCrossIcon } from '@/assets';
import { logger } from '@/lib/utils';
import { ImageScreen } from '@/components/patientApp/imageScreen';
import { SessionCompletedScreen } from '@/components/patientApp/sessionCompletedScreen';
import { useSuspenseQuery } from '@tanstack/react-query';
import { getProcedureQueryOptions } from '@/api/queries/procedures';
import { ProcedureType } from '@/api/schema/procedures';
import { AssessmentSessionCompletedScreen } from '@/components/patientApp/assessmentSessionCompletedScreen';

export const Route = createFileRoute('/_patient/patientApp/$procedureId/')({
  component: PatientApp,
});

function PatientApp() {
  const divRef = useRef<HTMLDivElement>(null);
  const params = Route.useParams();

  const { procedureId } = params as { procedureId: string };

  const { data: procedure } = useSuspenseQuery(
    getProcedureQueryOptions(procedureId),
  );

  const {
    currentStep,
    displayState,
    isConnected,
    connectionStatus,
    isWaitingForReconnect,
    reconnectAttempts,
  } = usePatientApp({
    onStepChange: (step, index, total) => {
      logger(`Step changed to: ${step.screen} (${index + 1}/${total})`);
    },
    onConnectionStatusChange: (connected) => {
      logger(
        'Patient app connection:',
        connected ? 'Connected' : 'Disconnected',
      );
    },
  });

  const shouldBlockExit
    = currentStep?.screen !== ProcedureScreen.SESSION_COMPLETED;

  // Warn user before leaving the page
  // This only works if user interaction has occurred on the page (click somewhere etc)
  useBlocker({
    enableBeforeUnload: shouldBlockExit,
    shouldBlockFn: () => {
      if (!shouldBlockExit) {
        return false;
      }

      const shouldLeave = confirm('Are you sure you want to leave?');
      return !shouldLeave;
    },
  });

  const renderCurrentScreen = useCallback(() => {
    if (currentStep === null) {
      if (!isConnected) {
        return (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4">
              <h2>Waiting for connection...</h2>
              { connectionStatus === 'Reconnecting' && (
                <p>
                  Reconnecting... (Attempt
                  { reconnectAttempts }
                  )
                </p>
              ) }
              { isWaitingForReconnect && (
                <p>Connection lost. Attempting to reconnect...</p>
              ) }
            </div>
          </div>
        );
      }

      return (
        <div className="flex-1 flex items-center justify-center">
          <h2 className="text-cue-reactivity-semi-bold">Loading...</h2>
        </div>
      );
    }

    switch (currentStep.screen) {
      case ProcedureScreen.INFO:
        return <InfoScreen config={ currentStep.config as InfoScreenConfig } />;

      case ProcedureScreen.VAS_SCALE:
      case ProcedureScreen.EMOTION_SCALE:
        return (
          <VasScaleScreen
            config={ currentStep.config as ScaleConfig }
            displayState={
              displayState[currentStep.id] as {
                value?: number;
                confirmed?: boolean;
              }
            }
          />
        );

      case ProcedureScreen.LOADING:
        return (
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-cue-reactivity-semi-bold">
              { (currentStep.config
                && (currentStep.config as LoadingConfig).message)
              ?? 'Loading...' }
            </h2>
          </div>
        );

      case ProcedureScreen.FOCUS_CROSS:
        return (
          <div className="flex-1 flex items-center justify-center">
            <img src={ FocusCrossIcon } alt="" />
          </div>
        );

      case ProcedureScreen.IMAGE_DISPLAY:
        return (
          <ImageScreen config={ currentStep.config as ImageDisplayConfig } />
        );

      case ProcedureScreen.SESSION_COMPLETED:
        return procedure.type === ProcedureType.ASSESSMENT
          ? (
            <AssessmentSessionCompletedScreen />
          )
          : (
            <SessionCompletedScreen />
          );

      case ProcedureScreen.WAITING_FOR_OPERATOR:
      case ProcedureScreen.EMPTY:
        // empty screen
        return <></>;

      default:
        return (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h2>
                Unknown screen type:
                { currentStep.screen }
              </h2>
            </div>
          </div>
        );
    }
  }, [
    currentStep,
    isConnected,
    displayState,
    connectionStatus,
    isWaitingForReconnect,
    reconnectAttempts,
  ]);

  return (
    <div
      ref={ divRef }
      className="flex-1 p-5xl flex flex-col bg-cue-reactivity-surface overflow-hidden
                text-cue-reactivity-black font-cue-reactivity"
    >
      { renderCurrentScreen() }
    </div>
  );
}
